import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Check, Settings2 } from "lucide-react";
import { useEffect, useState } from "react";

const TEXT_SECONDARY = "#58534c";

interface MicSelectorProps {
  selectedDeviceId: string | null;
  onSelect: (deviceId: string | null) => void;
  disabled?: boolean;
}

export function MicSelector({
  selectedDeviceId,
  onSelect,
  disabled,
}: MicSelectorProps) {
  const [open, setOpen] = useState(false);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);

  useEffect(() => {
    if (!open) return;
    const md = navigator.mediaDevices;
    if (!md?.enumerateDevices) {
      setDevices([]);
      return;
    }
    let cancelled = false;
    md.enumerateDevices()
      .then((all) => {
        if (cancelled) return;
        setDevices(all.filter((d) => d.kind === "audioinput"));
      })
      .catch(() => {
        if (!cancelled) setDevices([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Microphone settings"
          disabled={disabled}
          className="rounded-md p-1.5 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-40"
          style={{ color: TEXT_SECONDARY }}
        >
          <Settings2 className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="end" side="bottom">
        <p className="px-2 pb-2 text-xs font-medium" style={{ color: TEXT_SECONDARY }}>
          Microphone
        </p>
        <button
          type="button"
          onClick={() => {
            onSelect(null);
            setOpen(false);
          }}
          className={cn(
            "flex w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-zinc-50",
          )}
        >
          <span>System default</span>
          {selectedDeviceId === null && <Check className="h-4 w-4" />}
        </button>
        {devices.length > 0 ? (
          <div className="max-h-64 overflow-y-auto">
            {devices.map((d) => (
              <button
                key={d.deviceId}
                type="button"
                onClick={() => {
                  onSelect(d.deviceId);
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-zinc-50"
              >
                <span className="truncate">
                  {d.label || "Unnamed input"}
                </span>
                {selectedDeviceId === d.deviceId && (
                  <Check className="h-4 w-4 shrink-0" />
                )}
              </button>
            ))}
          </div>
        ) : (
          <p
            className="px-2 py-2 text-xs"
            style={{ color: TEXT_SECONDARY }}
          >
            Record once to populate the list (browsers hide mic labels until
            permission is granted).
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
